<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import type { ColDef } from 'ag-grid-community'
import { useEligibilityStore } from '@/stores/useEligibilityStore'

const props = defineProps<{
  isAdv?: boolean
  onViewDetails: (uuid: string) => void
}>()

const eligibilityStore = useEligibilityStore()
const { eligibilityHistory, isLoadingHistory } = storeToRefs(eligibilityStore)

const search = ref('')
let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(search, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    eligibilityStore.fetchEligibilityHistory(val.trim() ? { search: val.trim() } : {})
  }, 350)
})

function formatDate(dateStr: string) {
  if (!dateStr) return ''
  return new Intl.DateTimeFormat('fr-FR').format(new Date(dateStr))
}

const columnDefs = computed<ColDef[]>(() => {
  const cols: ColDef[] = [
    {
      field: 'dateCreated',
      headerName: 'Date',
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => formatDate(params.value),
      maxWidth: 120,
    },
    {
      field: 'company.name',
      headerName: 'Client',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.company?.name || '-',
    },
    {
      field: 'company.siret',
      headerName: 'SIRET',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => params.data?.company?.siret || '-',
    },
    {
      field: 'fulladdress',
      headerName: 'Adresse',
      filter: 'agTextColumnFilter',
      flex: 2,
    },
    {
      field: 'codeBatiment',
      headerName: 'Code immeuble',
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => params.value || '-',
    },
  ]

  if (props.isAdv) {
    cols.push({
      field: 'salesEngineer',
      headerName: 'Auteur',
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const a = params.data?.author
        if (!a) return '-'
        const name = [a.firstName, a.lastName].filter(Boolean).join(' ')
        return name || a.email || '-'
      },
      maxWidth: 180,
    })
  }

  cols.push({
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filter: false,
    maxWidth: 100,
    cellClass: 'grid-action',
    cellRenderer: (params: { data: { uuid: string } }) => {
      const button = document.createElement('button')
      button.className = 'btn btn-ghost btn-xs'
      button.innerText = 'Détails'
      button.addEventListener('click', () => props.onViewDetails(params.data.uuid))
      return button
    },
  })

  return cols
})
</script>

<template>
  <div class="card bg-base-100 shadow">
    <div class="card-body">
      <div v-if="isAdv" class="mb-4 max-w-sm">
        <TextInput
          v-model="search"
          label=""
          placeholder="Rechercher par client, adresse, code immeuble…"
          :icon="MagnifyingGlassIcon"
        />
      </div>

      <div v-if="isLoadingHistory" class="flex justify-center p-8">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
      <GridVue :list="eligibilityHistory" :cols="columnDefs">
        <template #title>
          <h2 class="card-title">Historique{{ isAdv ? ' de toutes les éligibilités' : ' de mes éligibilités' }}</h2>
        </template>
      </GridVue>
    </div>
  </div>
</template>
